/* eslint-disable react-refresh/only-export-components */
// context/hook 与组件同文件为有意模式;react-refresh 仅关注 HMR,不影响运行时。
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { QueryClientContext } from "@tanstack/react-query";
import i18n from "@/shared/lib/i18n";
import { api, ApiError, setToken, clearToken, getToken } from "@/shared/api";
import { ROLES, type RoleKey } from "@/shared/lib/rbac";
import { setVendorReveal } from "@/shared/lib/vendorMask";
import { permissionService, type MyPermissions, type ModulePerms } from "@/entities/permission";
import { resolveCompliance, type Compliance } from "@/entities/compliance";
import { getBrand } from "@/shared/brand/store";
import { isPartnerBrand } from "@/shared/brand/bootstrap";
import { pickLoginError } from "./pickLoginError";
import { moduleLockedNotice } from "@/shared/lib/forbiddenReason";
import {
  PASSWORD_CHANGE_CLAIM,
  isPasswordChangeRequired,
  markPasswordChangeRequired,
  usePasswordChangeRequired,
} from "@/shared/lib/passwordChangeRequired";

// V23 (2026-05-17 epic-2): RoleKey 派生自 src/lib/rbac.ts(单一源)。
// 旧 import { type RoleKey } from "@/contexts/AuthContext" 仍可用,re-export 保持兼容。
export { type RoleKey } from "@/shared/lib/rbac";

/** Token scope — 与后端 JWT scope 字段一致。决定 entry/路径前缀。PARTNER = OEM 渠道商管理员(A-P2)。 */
export type Scope = "PLATFORM" | "TENANT" | "MEMBER" | "PARTNER";

/**
 * Entry 期望的 scope。"AUTO" 用于 index.html 入口(主域 / 渠道域)— 登录走统一登录口
 * /auth/unified/login(1004,后端按 Host 认人),接受任一成功的 scope;启动时也不强制 scope match。
 * platform.html / tenant.html 仍传具体 scope 强校验;数组 = 接受其中任一
 * (platform.html 自 OEM W3 起 = ["PLATFORM","PARTNER"]:平台超管与渠道管理员共用后台壳)。
 */
export type ExpectedScope = Scope | "AUTO" | readonly Scope[];

/**
 * 登录口。"default" = 原有平台 / 租户口(按 entry 选);"partner" = 渠道管理员口
 * `/auth/partner/login`(只在渠道域名上认人,system 域后端一律 401)。
 */
export type LoginPortal = "default" | "partner";

/** entry 是否接受某 scope。AUTO 接受除 MEMBER 外的一切(MEMBER 走企微 H5)。 */
export function acceptsScope(expected: ExpectedScope, scope: Scope): boolean {
  if (expected === "AUTO") return scope !== "MEMBER";
  if (typeof expected === "string") return expected === scope;
  return expected.includes(scope);
}

export interface AuthUser {
  userId: number;
  username: string;
  name: string;
  email: string;
  role: RoleKey;
  roleLabel: string;
  scope: Scope;
  /** 平台超管(scope=PLATFORM)无租户归属,值为 null;租户/MEMBER 必填。 */
  tenantCode: string | null;
  tenantId: number | null;
  tenantName: string | null;
  /** OEM 渠道商管理员的渠道编码(scope=PARTNER 时必有);其余身份为 null / 缺省。 */
  partnerCode?: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** 期望 scope(由 entry 设置)。"AUTO"=主域兼容入口,任一 scope 都接受。 */
  expectedScope: ExpectedScope;
  /**
   * 当前登录用户的有效权限。null = 还在加载;空 modules = 已知用户无任何模块权限。
   * sidebar / 按钮可见性应该走这个,不要再硬编码 role 判断。
   */
  permissions: MyPermissions | null;
  permissionsLoading: boolean;
  /**
   * V27 (2026-05-20) — "视图角色":代客登录时 = TENANT_ADMIN (后端 /me/permissions 切),
   * 否则 = 真实身份。 sidebar / 按钮 visibility / 页面分支选择走这个,不再用 user.role。
   * user.role / user.scope 留给 banner / 退出代客按钮 / 真实身份审计 / 数据所有者判定。
   */
  effectiveRole: RoleKey | null;
  effectiveScope: Scope | null;
  login: (credential: string, password: string, portal?: LoginPortal) => Promise<LoginOutcome>;
  logout: () => void;
  refreshPermissions: () => Promise<void>;
  /**
   * 局部更新当前登录用户的展示字段(改完个人资料后调用),同步写回 localStorage。
   * 解决"改了名字但顶栏 / 头像仍显示旧名"——之前 user 只在登录时写入,资料更新后不刷新。
   */
  patchUser: (partial: Partial<AuthUser>) => void;
  /**
   * 0930:本会话必须先改密(首登强制改密未完成)。刷新后由 token 的 pwc claim / GET /me 恢复,
   * 后端 403 PASSWORD_CHANGE_REQUIRED 也会置位。ProtectedRoute 据此整站只渲染改密页。
   */
  passwordChangeRequired: boolean;
}

/** login() 的返回:调用方(LoginPage)据此决定是直接进站还是先弹强制改密。 */
export interface LoginOutcome {
  /** 2026-08-09 — 账号仍在用初始密码(0926 K 起=账号带强制改密标记,或存量 123456);老后端无此字段 → false。 */
  requirePasswordChange: boolean;
  /** 本次登录拿到的 scope(LoginPage 据此分流:PARTNER → /partner)。 */
  scope: Scope;
  /** 1030:本次登录的 userId(LoginPage 据此判断断线返回地址是不是本人的)。 */
  userId: number;
}

interface LoginResponse {
  userId: number;
  username: string;
  displayName: string;
  email: string;
  role: string;
  roleKey: string;
  roleLabel: string;
  accessToken: string;
  expiresInMs: number;
  /** 平台超管路径返回 null。 */
  tenantCode: string | null;
  tenantId: number | null;
  tenantName: string | null;
  /**
   * 2026-08-09 — 仍在用初始密码(0926 K 起=账号标记或存量 123456);老后端不回此字段。
   * ★B1 0927 起平台登录口(/auth/login)对刚建号 / 刚被重置密码的平台管理员也返 true ——
   *   LoginPage 的强制改密弹窗不分端(两个端点的 outcome 同一条路),改密走同一个 PUT /me/password。
   */
  requirePasswordChange?: boolean;
  /** OEM A-P2:渠道管理员登录时 = 渠道编码;平台 / 租户口为 null(老后端无此字段)。 */
  partnerCode?: string | null;
}

/** 统一登录口不存在(老后端 404 / 405)。PARTNER_NOT_FOUND 也是 404,那是「未知子区」的业务错,不算。 */
function isMissingEndpoint(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.errorCode === "PARTNER_NOT_FOUND") return false;
  return err.httpStatus === 404 || err.httpStatus === 405;
}

/** 两段式里首选口失败后要不要打备选口:只有「这个口不认这个账号」(401 / 404 / 400)才试;429 / 403 / 5xx 直接报。 */
export function shouldTryFallback(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  const status = err.httpStatus;
  return status === 401 || status === 404 || status === 400 || status === 405;
}

/**
 * 1004 前的 AUTO 两段式(只给没有统一登录口的老后端兜底):渠道域只走租户口;主域按凭据格式先选一个口,失败再试另一个。
 */
async function legacyAutoLogin(
  credential: string,
  password: string,
  partnerHost: boolean
): Promise<LoginResponse> {
  if (partnerHost) {
    return api.post<LoginResponse>("/auth/tenant/login", { credential, password });
  }
  const trimmed = credential.trim();
  const tenantFirst = trimmed.includes("@") || /^\d{11}$/.test(trimmed);
  const primary = tenantFirst ? "/auth/tenant/login" : "/auth/login";
  const fallback = tenantFirst ? "/auth/login" : "/auth/tenant/login";
  try {
    return await api.post<LoginResponse>(primary, { credential, password });
  } catch (firstErr) {
    // X1 1013:已被限流 / 账号停用等明确拒绝时不再打备选口 —— 后端频控按尝试计数,再打一次只会把恢复时间往后推。
    if (!shouldTryFallback(firstErr)) throw firstErr;
    try {
      return await api.post<LoginResponse>(fallback, { credential, password });
    } catch (secondErr) {
      throw pickLoginError(firstErr, secondErr);
    }
  }
}

/** 代客元数据在不在(ImpersonationContext 的 IMPERSONATION_META_KEY,字面量同源于 httpClient 登出清理)。 */
function isImpersonatingNow(): boolean {
  try {
    return !!localStorage.getItem("xbgeo-impersonation");
  } catch {
    return false;
  }
}

/** 从 role 推断 scope(后端 JWT 已带 scope 但前端 LoginResponse 暂不包含,本地推断)。 */
export function scopeFromRole(role: string): Scope {
  if (role === ROLES.PLATFORM_ADMIN) return "PLATFORM";
  if (role === ROLES.MEMBER) return "MEMBER";
  if (role === ROLES.PARTNER_ADMIN) return "PARTNER";
  return "TENANT";
}

interface JwtPayload {
  sub?: string;
  role?: RoleKey;
  scope?: Scope;
  tenantCode?: string;
  partnerCode?: string;
  exp?: number;
  /** 0930:「待改密会话」(账号带首登强制改密标记时登录签出的 token)。 */
  [PASSWORD_CHANGE_CLAIM]?: boolean;
}

/**
 * 解析 JWT payload(L2 修复)。
 *
 * <p>JWT 由后端签名 + 客户端无法伪造其内容,因此 payload 中的 role/scope/tenantCode 是**唯一权威**来源。
 * 之前 loadUser 直接信任 localStorage 解出的对象,本地用户改 localStorage role 字段可抬升 UI 权限
 * (API 仍受 JWT 拦,但 sidebar / 路由 / 按钮渲染按假 role 走)。现在用 JWT payload 覆盖 localStorage
 * 中的 role/scope/tenantCode,本地篡改对 UI 显示也无效。
 *
 * <p>注意:本函数仅解码不验签。验签靠后端;前端解码只用于显示,信任度由 JWT 签名背书。
 */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // base64url → base64
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = "xbgeo-auth";
const PERMS_STORAGE_KEY = "xbgeo-permissions";

/**
 * OEM H1(1001):权限缓存的后台刷新节奏。/me/permissions 自本批起 = 角色 ∩ 租户已开通(后端唯一真源),
 * 平台 / 渠道开关租户模块后,租户端不必重登 —— 下一次拉取即生效:
 * - 登录后 / 页面加载(即使有 localStorage 缓存也重拉一次,缓存只用来防首帧闪空);
 * - 窗口重新获得焦点 / 标签页切回可见(节流 {@link PERMS_FOCUS_MIN_INTERVAL_MS},切窗口不刷屏);
 * - 每 {@link PERMS_POLL_INTERVAL_MS} 后台轮一次。
 * 不做实时推送。
 */
export const PERMS_POLL_INTERVAL_MS = 5 * 60_000;
export const PERMS_FOCUS_MIN_INTERVAL_MS = 30_000;
/** login() 刚拉完、紧接着 user 变更 effect 又要拉:这个窗口内视为新鲜,不重复请求。 */
const PERMS_DEDUPE_MS = 5_000;

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw) as AuthUser;
    // 老缓存可能没 scope 字段,从 role 推断
    if (!u.scope) {
      u.scope = scopeFromRole(u.role);
    }
    // L2: 用 JWT payload 中的 role/scope/tenantCode 覆盖 localStorage(本地篡改 UI 失效)
    const token = getToken();
    if (token) {
      const claims = decodeJwtPayload(token);
      if (claims?.role) u.role = claims.role;
      if (claims?.scope) u.scope = claims.scope;
      if (claims?.tenantCode !== undefined) u.tenantCode = claims.tenantCode;
      if (claims?.partnerCode !== undefined) u.partnerCode = claims.partnerCode;
      // 0930:刷新后第一帧就恢复「必须先改密」—— 此前这个状态只在 LoginPage 内存里,刷新即丢、直接进站。
      if (claims?.[PASSWORD_CHANGE_CLAIM] === true) markPasswordChangeRequired(true);
    }
    return u;
  } catch {
    return null;
  }
}

function loadCachedPermissions(): MyPermissions | null {
  try {
    const raw = localStorage.getItem(PERMS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * 当前 entry 期望的 scope。
   * - 具体 scope("PLATFORM"/"TENANT"/"MEMBER"):登录走对应端点,scope mismatch 强制 logout
   * - "AUTO":index 入口,走统一登录口(一次请求);不强制 scope match
   * 默认 "AUTO"(主域 index.html 兼容入口)。
   */
  expectedScope?: ExpectedScope;
}

export function AuthProvider({ children, expectedScope = "AUTO" }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(loadUser);
  // 用 localStorage 缓存权限,避免页面刷新瞬间整个 sidebar 闪一下空。后端返回再覆盖。
  const [permissions, setPermissions] = useState<MyPermissions | null>(loadCachedPermissions);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  // 上次成功拉到权限的时刻 + 序列化快照(节流 / 判「变没变」用,不进渲染)。
  const permsFetchedAtRef = useRef(0);
  const permsSnapshotRef = useRef<string | null>(permissions ? JSON.stringify(permissions) : null);
  // AuthProvider 在 App 里挂在 QueryClientProvider 之内;单测里可能没有 —— 用 context 取,缺省不报错。
  const queryClient = useContext(QueryClientContext);

  const refreshPermissions = useCallback(async () => {
    // OEM F2(0930):待改密会话(pwc token)拉 /me/permissions 必回 403 PASSWORD_CHANGE_REQUIRED ——
    // 首登强制改密时 login() 与「user 变了就补拉」的 effect 都会先打一枪 403,改密重登后才 200。
    // 改密完成(重登拿到新 token、信号清掉)之前一律不拉;重登的 login() 会在清信号后再拉。
    if (isPasswordChangeRequired()) return;
    setPermissionsLoading(true);
    try {
      const data = await permissionService.getMine();
      permsFetchedAtRef.current = Date.now();
      const snapshot = JSON.stringify(data);
      const changed = snapshot !== permsSnapshotRef.current;
      permsSnapshotRef.current = snapshot;
      if (changed) {
        // 内容没变不 setState:后台轮询 / 回焦刷新不触发整树重渲。
        setPermissions(data);
        localStorage.setItem(PERMS_STORAGE_KEY, snapshot);
        // OEM H1:开通集变了 → 工作台 / 路由门共用的 /me/modules 缓存(staleTime 5 分钟)一起作废,
        // 否则「侧栏已出现、卡墙还按旧 disabled 藏着」要等它自己过期。
        void queryClient?.invalidateQueries({ queryKey: ["dashboard-modules"] });
      }
    } catch (err) {
      // 静默降级:保留 localStorage 缓存(可能是 401/网络抖动)。401 会被 api 层清 token + 跳登录。
      console.warn("failed to load /me/permissions", err);
    } finally {
      setPermissionsLoading(false);
    }
  }, [queryClient]);

  const login = useCallback(
    async (credential: string, password: string, portal: LoginPortal = "default") => {
      // entry 决定调哪个 backend 端点:
      // - PLATFORM/TENANT 严格走对应端点,scope mismatch 拒绝
      // - AUTO(主域 / 渠道域 index 入口):统一登录口 /auth/unified/login,一次请求(1004)
      let res: LoginResponse;
      if (portal === "partner") {
        // OEM 渠道管理员口:entry 不接受 PARTNER(tenant.html)时不发请求。
        if (!acceptsScope(expectedScope, "PARTNER")) {
          throw new Error(i18n.t("impersonation.scopeMismatchTenant"));
        }
        res = await api.post<LoginResponse>("/auth/partner/login", { credential, password });
      } else if (expectedScope === "AUTO") {
        // 1004(O1 / O2 / P3-1):主域与渠道域的默认表单都走统一登录口 —— 一次请求、一次频控,后端按 Host 认
        // 平台超管 / 租户 / 本渠道管理员;渠道管理员走错域名且密码正确时回 PARTNER_DOMAIN_MISMATCH + 专属地址。
        // 此前按凭据格式先打一个口、401 再打另一个:每次登录都先吃一个 401(控制台报错 + 多占一次频控)。
        try {
          res = await api.post<LoginResponse>("/auth/unified/login", { credential, password });
        } catch (err) {
          if (!isMissingEndpoint(err)) throw err;
          // 老后端(还没有统一口):回落旧的两段式,发版先后顺序不卡登录。
          res = await legacyAutoLogin(credential, password, isPartnerBrand(getBrand()));
        }
      } else {
        const endpoint = acceptsScope(expectedScope, "PLATFORM")
          ? "/auth/login"
          : "/auth/tenant/login";
        res = await api.post<LoginResponse>(endpoint, { credential, password });
      }
      const scope = scopeFromRole(res.role);
      if (expectedScope !== "AUTO" && !acceptsScope(expectedScope, scope)) {
        // 防御性兜底:后端返回了不该来的角色 → 立刻拒绝(只在严格模式)
        clearToken();
        throw new Error(
          acceptsScope(expectedScope, "PLATFORM")
            ? i18n.t("impersonation.scopeMismatchPlatform")
            : i18n.t("impersonation.scopeMismatchTenant")
        );
      }
      setToken(res.accessToken);
      const u: AuthUser = {
        userId: res.userId,
        username: res.username,
        name: res.displayName,
        email: res.email,
        // V23 backend 只回 role,旧 LoginResponse type 同时含 roleKey;fallback 兼容两端
        role: (res.roleKey ?? res.role) as RoleKey,
        roleLabel: res.roleLabel,
        scope,
        tenantCode: res.tenantCode ?? null,
        tenantId: res.tenantId ?? null,
        tenantName: res.tenantName ?? res.tenantCode ?? null,
        partnerCode: res.partnerCode ?? null,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
      // 先置位再 setUser:任何受保护路由首帧就能看到正确的改密状态。
      markPasswordChangeRequired(res.requirePasswordChange ?? false);
      setUser(u);
      await refreshPermissions();
      return {
        requirePasswordChange: res.requirePasswordChange ?? false,
        scope,
        userId: res.userId,
      };
    },
    [refreshPermissions, expectedScope]
  );

  const logout = useCallback(() => {
    // 先通知后端删 Redis sid 锚点(fire-and-forget,失败不阻塞前端登出)
    void api.post("/auth/logout", null).catch(() => {
      // 网络挂 / token 已失效都吞掉 — 本地清理仍要继续
    });
    clearToken();
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PERMS_STORAGE_KEY);
    // ★不在这里清改密信号:它是外部 store,一变就同步重绘,而此刻 user 还没置空 →
    // ProtectedRoute 会先渲染一帧业务页、不带 token 扇出一批请求吃 403 并弹「请求失败」(0930 演示机实测)。
    // 改由下方 effect 在 user 置空之后再清。
    setUser(null);
    setPermissions(null);
    permsSnapshotRef.current = null;
    permsFetchedAtRef.current = 0;
  }, []);

  useEffect(() => {
    if (!user) markPasswordChangeRequired(false);
  }, [user]);

  const patchUser = useCallback((partial: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage 不可用时静默忽略,内存态仍更新
      }
      return next;
    });
  }, []);

  // 已登录(含刷新后从 localStorage 恢复)→ 拉一次。OEM H1:有缓存也重拉(缓存只防首帧闪空,
  // 开通/关闭模块后不必重登);login() 刚拉过的(PERMS_DEDUPE_MS 内)不重复。
  // token 由 api 层从 localStorage 自动注入。
  useEffect(() => {
    if (!user || permissionsLoading) return;
    if (permissions && Date.now() - permsFetchedAtRef.current < PERMS_DEDUPE_MS) return;
    void refreshPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // OEM H1:回焦 / 切回可见(节流)+ 每 5 分钟后台刷新权限。未登录不挂。
  const loggedIn = !!user;
  useEffect(() => {
    if (!loggedIn) return;
    const refreshIfStale = (minAgeMs: number) => {
      if (Date.now() - permsFetchedAtRef.current < minAgeMs) return;
      void refreshPermissions();
    };
    const onFocus = () => refreshIfStale(PERMS_FOCUS_MIN_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const timer = window.setInterval(() => void refreshPermissions(), PERMS_POLL_INTERVAL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(timer);
    };
  }, [loggedIn, refreshPermissions]);

  // 0930:已登录(含刷新后从 localStorage 恢复)→ 问一次 GET /me 的 requirePasswordChange。
  // token 带 pwc claim 时首帧已置位;这里兜「老 token 没有 claim、但账号仍带强制改密标记」的情形。
  // 代客态不问(代客 token 永不是待改密会话,/me 返回的是平台超管本人)。
  const userId = user?.userId;
  useEffect(() => {
    if (userId == null || isImpersonatingNow()) return;
    let cancelled = false;
    api
      .get<{ requirePasswordChange?: boolean }>("/me")
      .then((me) => {
        if (!cancelled && me?.requirePasswordChange === true) markPasswordChangeRequired(true);
      })
      .catch(() => {
        // 401 由 api 层处理;403 PASSWORD_CHANGE_REQUIRED 已在 httpClient 置位;其余静默
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const passwordChangeRequired = usePasswordChangeRequired();

  // 改密信号清掉(且仍登录、权限还没拿到)→ 补拉一次;待改密期间 refreshPermissions 是空转的。
  useEffect(() => {
    if (!passwordChangeRequired && user && !permissions && !permissionsLoading) {
      void refreshPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordChangeRequired]);

  // Scope 守卫:已登录用户 scope 与 entry 不匹配 → 强制 logout(例如平台用户访问 tenant.example.com)
  // AUTO 模式(主域兼容入口)对 PLATFORM/TENANT 互通,但 MEMBER 必须走 wecom-h5 entry。
  useEffect(() => {
    if (!user || !user.scope) return;
    // AUTO 模式额外保护:MEMBER 不能登录主域 SPA(否则可看完整管理台 UI 结构,H1)
    if (expectedScope === "AUTO" && user.scope === ROLES.MEMBER) {
      console.warn("MEMBER 不能登录主域 SPA,强制 logout 提示走企微 H5 入口");
      logout();
      return;
    }
    if (expectedScope !== "AUTO" && !acceptsScope(expectedScope, user.scope)) {
      console.warn(
        `Scope mismatch: user.scope=${user.scope} but entry expects ${expectedScope}, force logout`
      );
      logout();
    }
  }, [user, expectedScope, logout]);

  // V27 (2026-05-20) — 派生视图角色: permissions.role(代客时后端返 TENANT_ADMIN)
  // 优先;permissions 还没拉到时 fallback 到 user.role。
  const effectiveRole: RoleKey | null =
    (permissions?.role as RoleKey | undefined) ?? user?.role ?? null;
  const effectiveScope: Scope | null = effectiveRole ? scopeFromRole(effectiveRole) : null;

  // ★OEM 0929(C-C):厂商真名只对「平台超管 · 非代客态」放行(档位名附真名 + 错误不脱敏)。
  //   代客态看到的必须和租户一模一样。代客进出都是整页跳转,本值在子树首次渲染前就定好 ——
  //   所以在渲染期同步写模块开关(幂等),不放 useEffect(那样首屏会先按旧值渲染一遍且不重渲)。
  setVendorReveal(
    user?.role === ROLES.PLATFORM_ADMIN &&
      effectiveRole === ROLES.PLATFORM_ADMIN &&
      !isImpersonatingNow()
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        expectedScope,
        permissions,
        permissionsLoading,
        effectiveRole,
        effectiveScope,
        login,
        logout,
        refreshPermissions,
        patchUser,
        passwordChangeRequired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * 单个模块的权限读取:`usePermission("ai_poster")`。
 * 返回该模块的 perms,未授权返回 null;调用方按需判断 page/view/edit/publish。
 */
export function usePermission(moduleKey: string): ModulePerms | null {
  const { permissions } = useAuth();
  return permissions?.modules?.[moduleKey] ?? null;
}

/** 模块"是否在 sidebar 可见":page !== 'none'。其余的用 usePermission 读细粒度。 */
export function useModuleVisible(moduleKey: string): boolean {
  const perms = usePermission(moduleKey);
  return !!perms && perms.page !== "none";
}

/**
 * OEM H1 历史作品口径:模块「已开通」或「在 historyModules 里(未开通 / 已停用 / 已过期)」→ 作品线可见。
 * 只给「作品」页 / 工作台在途与最近作品用;入口类(侧栏 / ⌘K / 卡墙 / 快捷)仍只认 {@link useModuleVisible}。
 */
export function useModuleHistoryVisible(moduleKey: string): boolean {
  const { permissions } = useAuth();
  const visible = useModuleVisible(moduleKey);
  return visible || !!permissions?.historyModules?.includes(moduleKey);
}

/** 只读态的一句提示:被服务商暂停(pausedModules)与未开通分开说(OEM 第二批 A7)。 */
export function useModuleLockedNotice(moduleKey: string): string {
  const { permissions } = useAuth();
  return moduleLockedNotice(moduleKey, permissions?.pausedModules);
}

/** 模块处于「只能看历史、不能新建」态:不在已开通里、但在 historyModules 里。 */
export function useModuleLocked(moduleKey: string): boolean {
  const { permissions } = useAuth();
  const visible = useModuleVisible(moduleKey);
  return !visible && !!permissions?.historyModules?.includes(moduleKey);
}

/**
 * 1011 算法备案:当前租户的合规配置(`/me/permissions.compliance`)。
 * 字段缺失 / 权限未拉到 / 不在 AuthProvider 内(单测、独立入口)→ 代码兜底:
 * 四项全 false(1011 拍板默认全关)。
 */
export function useCompliance(): Compliance {
  const ctx = useContext(AuthContext);
  return resolveCompliance(ctx?.permissions?.compliance);
}
